import React from "react";
import { Link } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import {
  ShieldCheck,
  HardHat,
  GitCompareArrows,
  CalendarDays,
  CalendarOff,
  ClipboardCheck,
  Map as MapIcon,
  UserCircle2,
  FileText,
  ScanFace,
  Coffee,
} from "lucide-react";

/* ------------------------------------------------------------------ TILES */
// One unified theme — slate-900 surface matching the sidebar, with an
// amber-300 icon accent (same family the active sidebar item uses).
// No more rainbow colours.

function Tile({ icon: Icon, label, sublabel, to, testid }) {
  return (
    <Link to={to} data-testid={testid} aria-label={label} className="block">
      <div
        className="group relative flex h-32 w-full flex-col items-center justify-center gap-2.5 overflow-hidden rounded-xl border border-slate-200 bg-white px-3 py-3 text-slate-800 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-amber-400 hover:shadow-md hover:shadow-amber-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2"
      >
        {/* subtle amber glow accent on hover */}
        <span
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-400/70 to-transparent opacity-0 transition-opacity duration-200 group-hover:opacity-100"
          aria-hidden
        />
        <div className="grid h-10 w-10 place-items-center rounded-lg border border-amber-200 bg-amber-50 transition-colors duration-200 group-hover:border-amber-300 group-hover:bg-amber-100">
          <Icon className="h-5 w-5 text-amber-600" strokeWidth={1.75} />
        </div>
        <div className="text-center">
          <div className="text-[13px] font-semibold leading-tight text-slate-900">
            {label}
          </div>
          {sublabel && (
            <div className="mt-1 text-[9px] font-medium uppercase tracking-wider text-slate-500">
              {sublabel}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

// A non-interactive placeholder that fills the trailing grid slot so the
// layout reads as 2 perfectly uniform rows of 5. Carries a light-hearted
// note instead of empty whitespace.
function FunTile() {
  return (
    <div
      data-testid="att-hub-fun"
      className="relative flex h-32 w-full flex-col items-center justify-center gap-2 overflow-hidden rounded-xl border border-dashed border-slate-300 bg-slate-50 px-3 py-3 text-slate-600"
      aria-hidden="true"
    >
      <div className="grid h-10 w-10 place-items-center rounded-lg border border-amber-200/80 bg-amber-50">
        <Coffee className="h-5 w-5 text-amber-500" strokeWidth={1.75} />
      </div>
      <div className="text-center">
        <div className="text-[13px] font-semibold leading-tight text-slate-700">
          On a chai break
        </div>
        <div className="mt-1 text-[9px] font-medium uppercase tracking-wider text-slate-400">
          More tiles brewing&nbsp;·&nbsp;come back later
        </div>
      </div>
    </div>
  );
}

const TILES = [
  {
    to: "/admin/attendance/pmc",
    label: "PMC Worker Captures",
    sublabel: "By ITCOT supervisors",
    icon: ShieldCheck,
    testid: "att-hub-pmc",
  },
  {
    to: "/admin/attendance/contractor",
    label: "Contractor Worker Captures",
    sublabel: "By contractor supervisors",
    icon: HardHat,
    testid: "att-hub-contractor",
  },
  {
    to: "/admin/attendance/compare",
    label: "PMC ↔ Contractor",
    sublabel: "Side-by-side compare",
    icon: GitCompareArrows,
    testid: "att-hub-compare",
  },
  {
    to: "/admin/attendance/calendar",
    label: "Monthly Calendar",
    sublabel: "P / A / H matrix",
    icon: CalendarDays,
    testid: "att-hub-calendar",
  },
  {
    to: "/admin/attendance/supervisors",
    label: "Supervisor Self-Attendance",
    sublabel: "ITCOT selfies",
    icon: UserCircle2,
    testid: "att-hub-supervisors",
  },
  {
    to: "/admin/attendance/itcot-hourly",
    label: "ITCOT Hourly Attendance",
    sublabel: "Hourly selfie grid",
    icon: ScanFace,
    testid: "att-hub-itcot-hourly",
  },
  {
    to: "/admin/holidays",
    label: "Holidays",
    sublabel: "Counted as Present",
    icon: CalendarOff,
    testid: "att-hub-holidays",
  },
  {
    to: "/admin/attendance/permissions",
    label: "Permission Requests",
    sublabel: "Approve out-of-window",
    icon: ClipboardCheck,
    testid: "att-hub-permissions",
  },
  {
    to: "/admin/gps",
    label: "GPS Trail",
    sublabel: "Live movement map",
    icon: MapIcon,
    testid: "att-hub-gps",
  },
  {
    to: "/admin/feedback",
    label: "Monthly Feedback",
    sublabel: "PO & Association forms",
    icon: FileText,
    testid: "att-hub-feedback",
  },
];

export default function AttendanceHubPage() {
  return (
    <AppShell title="Attendance" subtitle="Tap a tile to manage that area">
      {/* Two uniform rows of 5 on large screens = 9 tiles + 1 fun placeholder.
          Responsive fallbacks keep tiles tidy on smaller widths. */}
      <div
        className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5"
        data-testid="attendance-tile-grid"
      >
        {TILES.map((t) => (
          <Tile key={t.to} {...t} />
        ))}
        <FunTile />
      </div>
    </AppShell>
  );
}
